import React, { useState } from 'react';
import { CheckCircle, Clock, XCircle, Trash2, Shield } from 'lucide-react';

/**
 * Implementation Outcome Component
 * For IT staff to record implementation results
 */
const ImplementationOutcome = ({ 
  onImplementationOutcome, 
  ticketType 
}) => {
  const [outcome, setOutcome] = useState('');
  const [deallocateAssets, setDeallocateAssets] = useState(false);
  const [deactivateAccess, setDeactivateAccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!outcome) return;
    
    setIsSubmitting(true);
    try {
      await onImplementationOutcome(outcome, deallocateAssets, deactivateAccess);
      setOutcome('');
      setDeallocateAssets(false);
      setDeactivateAccess(false);
    } catch (error) {
      console.error('Error recording outcome:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const outcomeOptions = [
    { value: 'successful', label: 'Successful', icon: CheckCircle, color: 'green' },
    { value: 'extension', label: 'Extension Requested', icon: Clock, color: 'yellow' },
    { value: 'unsuccessful', label: 'Unsuccessful', icon: XCircle, color: 'red' }
  ];

  // Don't show Access Management Actions - integrations are handled separately
  // For access_termination, these are handled as workflow integration steps
  // For access_modification (EDAMS), there are no portal integrations (external system)
  const showAccessOptions = false;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center mb-4">
        <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Implementation Outcome</h3>
      </div>
      
      <p className="text-sm text-gray-600 mb-6">
        Record the outcome of implementing this access request.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Implementation Result
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {outcomeOptions.map(({ value, label, icon: Icon, color }) => (
              <button
                key={value}
                onClick={() => setOutcome(value)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  outcome === value
                    ? `border-${color}-500 bg-${color}-50`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col items-center">
                  <Icon className={`w-6 h-6 mb-2 ${
                    outcome === value ? `text-${color}-600` : 'text-gray-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    outcome === value ? `text-${color}-900` : 'text-gray-700'
                  }`}>
                    {label}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {showAccessOptions && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Access Management Actions</h4>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={deallocateAssets}
                  onChange={(e) => setDeallocateAssets(e.target.checked)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Trash2 className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-700">Deallocate Assets</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={deactivateAccess}
                  onChange={(e) => setDeactivateAccess(e.target.checked)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Shield className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-700">Deactivate Portal Access</span>
              </label>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!outcome || isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Recording...' : 'Record Outcome'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImplementationOutcome;
